import { Building2, Plus } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api, type Organization } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { userHasGlobalScope } from "../../../auth/scopes";
import { useAuth } from "../../../contexts/AuthContext.tsx";
import FormattedDate from "../../../components/FormattedDate";
import IdentityCell from "../../../components/IdentityCell.tsx";
import LogoBadge from "../../../components/LogoBadge.tsx";
import PageHeader from "../../../components/PageHeader.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../../components/filters/specs.ts";
import { useFilters, useLocalFilterState } from "../../../components/filters/useFilters.ts";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { formatNumber } from "../../../utils/format.ts";

/** An organization's avatar: a building in a tile, so orgs never look like tenants (initials/logos). */
// Search only.
const NO_FILTERS: FilterSpec[] = [];

function OrganizationAvatar() {
  return (
    <LogoBadge size={32}>
      <Building2 fontSize="small" sx={{ color: "text.secondary" }} />
    </LogoBadge>
  );
}

function OrganizationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isGlobalAdmin = userHasGlobalScope(user, "users:manage");

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const filterState = useLocalFilterState();
  const filters = useFilters(NO_FILTERS, { state: filterState, search: { param: "q", placeholder: "Search name" } });

  const { data: orgs = [], isLoading } = useQuery({
    queryKey: keys.organization.list(),
    queryFn: api.getOrganizations,
  });

  const q = (filters.get("q") ?? "").trim().toLowerCase();
  const visible = q ? orgs.filter((o) => o.name.toLowerCase().includes(q)) : orgs;

  const createMutation = useMutation({
    mutationFn: (name: string) => api.createOrganization({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.organization.all });
      setCreateOpen(false);
      setNewName("");
      toast.success("Organization created");
    },
  });

  const submitCreate = (e: FormEvent) => {
    e.preventDefault();
    if (newName.trim() && !createMutation.isPending) createMutation.mutate(newName.trim());
  };

  const orgsWord = orgs.length === 1 ? "organization" : "organizations";
  const count = isLoading
    ? undefined
    : q
      ? `${formatNumber(visible.length)} of ${formatNumber(orgs.length)} ${orgsWord}`
      : `${formatNumber(orgs.length)} ${orgsWord}`;

  return (
    <Box>
      <PageHeader
        title="Organizations"
        description="Customer organizations, their tenants and members"
        actions={
          isGlobalAdmin && (
            <Button variant="contained" startIcon={<Plus />} onClick={() => setCreateOpen(true)}>
              New organization
            </Button>
          )
        }
      />
      <TableCard
        loading={isLoading}
        toolbar={<FilterBar filters={filters} count={count} />}
      >
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>Tenants</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((org: Organization) => (
              <TableRow
                key={org.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  // The name is a link already; don't navigate twice.
                  if ((e.target as HTMLElement).closest("a")) return;
                  navigate(`/settings/organizations/${org.id}`);
                }}
              >
                <TableCell>
                  <IdentityCell
                    avatar={<OrganizationAvatar />}
                    name={org.name}
                    to={`/settings/organizations/${org.id}`}
                  />
                </TableCell>
                <TableCell>
                  {org.tenant_count === 0 ? (
                    <Tooltip title="No tenant — members can log in but see an empty account. Onboarding is incomplete.">
                      <Chip size="small" color="warning" label="No tenant" />
                    </Tooltip>
                  ) : (
                    formatNumber(org.tenant_count)
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                  <FormattedDate value={org.created_at} />
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <EmptyTableRow colSpan={3}>
                {orgs.length === 0 ? "No organizations yet" : "No organizations match your search"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={submitCreate}>
          <DialogTitle>New organization</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Organization name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" loading={createMutation.isPending} disabled={!newName.trim()}>
              Create organization
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}

export default OrganizationsPage;
